import React, { useCallback, useEffect, useRef } from 'react'
import { Card as CardData, CardType, GamePhase, GameState, Player, CardColor } from '../types'
import * as gameLogic from '../services/gameLogic'
import { isValidCounter } from '../services/ai'
import { UI_ANCHOR_IDS, uiAnchorRegistry } from '../services/uiAnchorRegistry'

type ExecuteGameAction = (action: any, fn: (state: GameState) => GameState) => void

export const useBoardInteractions = (
  params: {
    gameState: GameState | null,
    setGameState: (u: any) => void,
    localPlayerId: number,
    playerIsReady: boolean,
    executeGameAction: ExecuteGameAction,
    groupHandForDisplay: (hand: CardData[]) => { key: string; topCard: CardData; cards: CardData[]; count: number }[],
    currentActivation: any,
    setDraggedCardInfo: (info: { card: CardData; rotation: number; source: string } | null) => void,
    setShowDefenseGhost: (v: boolean) => void,
    setHandCardRotations: (u: (prev: Record<string, number>) => Record<string, number>) => void,
    draggedCardInfo: { card: CardData; rotation: number; source: string } | null,
    setTargeting: (t: { card: any; startRect: DOMRect } | null) => void,
    setMousePos: (p: { x: number; y: number } | null) => void,
    targeting: { card: any; startRect: DOMRect } | null,
    aiAvatarRef: React.RefObject<HTMLDivElement>,
    playerAvatarRef: React.RefObject<HTMLDivElement>,
    handleActivateCard: (cardId: string, rotation: number, targetId?: number) => void,
    handleMageCastFromHandWithTarget?: (cardId: string, rotation: number, targetId?: number, targetActivationId?: string) => void,
    onMageCastSuccess?: () => void,
    activationZonesRef?: React.MutableRefObject<Array<{ activationId: string; rect: DOMRect }>>,
    setProtectorChoiceCardId: (cardId: string | null) => void,
    handleAstridBlastWithTarget?: (color: CardColor, targetId: number) => void,
  }
) => {
  const {
    gameState,
    setGameState,
    localPlayerId,
    playerIsReady,
    executeGameAction,
    groupHandForDisplay,
    currentActivation,
    setDraggedCardInfo,
    setShowDefenseGhost,
    setHandCardRotations,
    draggedCardInfo,
    setTargeting,
    setMousePos,
    targeting,
    aiAvatarRef,
    playerAvatarRef,
    handleActivateCard,
    handleMageCastFromHandWithTarget,
    handleAstridBlastWithTarget,
    onMageCastSuccess,
    activationZonesRef,
    setProtectorChoiceCardId,
  } = params

  // Use ref to always have the latest targeting value in event handlers (avoids stale closure)
  // IMPORTANT: Update synchronously during render, NOT in useEffect (which is async)
  const targetingRef = useRef(targeting);
  targetingRef.current = targeting; // Synchronous update on every render

  const getLocalAvatarRect = useCallback(
    () => uiAnchorRegistry.getRect(UI_ANCHOR_IDS.localAvatar) ?? playerAvatarRef.current?.getBoundingClientRect() ?? null,
    [playerAvatarRef]
  )
  const getOpponentAvatarRect = useCallback(
    () => uiAnchorRegistry.getRect(UI_ANCHOR_IDS.opponentAvatar) ?? aiAvatarRef.current?.getBoundingClientRect() ?? null,
    [aiAvatarRef]
  )
  const isDropInActivationArea = useCallback((clientX: number, clientY: number) => {
    const boardRect = uiAnchorRegistry.getRect(UI_ANCHOR_IDS.gameBoard) ?? uiAnchorRegistry.getRect(UI_ANCHOR_IDS.shellFrame)
    if (boardRect) {
      const centerX = boardRect.left + boardRect.width / 2
      const halfWidth = Math.min(300, boardRect.width * 0.3)
      const maxY = boardRect.top + boardRect.height * 0.6
      return clientY >= boardRect.top && clientY <= maxY && clientX >= centerX - halfWidth && clientX <= centerX + halfWidth
    }

    const screenCenter = window.innerWidth / 2
    return clientY < window.innerHeight * 0.6 && clientX > screenCenter - 300 && clientX < screenCenter + 300
  }, [])

  const handleCardDragStart = useCallback((card: CardData, rotation: number, source: string) => {
    setDraggedCardInfo({ card, rotation, source })
  }, [setDraggedCardInfo])

  const handleCardDragEnd = useCallback(() => {
    setDraggedCardInfo(null)
    setShowDefenseGhost(false)
  }, [setDraggedCardInfo, setShowDefenseGhost])

  const handlePrepareCard = useCallback((cardId: string, rotation: number) => {
    if (!gameState || gameState.phase !== GamePhase.Preparation || playerIsReady) return
    executeGameAction(
      { type: 'PREPARE_CARD', playerId: localPlayerId, cardId, rotation },
      (state) => gameLogic.prepareCard(state, localPlayerId, cardId, rotation)
    )
  }, [gameState, playerIsReady, localPlayerId, executeGameAction])

  const handlePreparedCardRotation = useCallback((cardId: string, newRotation: number) => {
    if (!gameState || !gameState.players) return

    // Check if local player is under Discerning Eye effect (opponent can see our cards)
    // If so, sync the rotation via game action so opponent sees the rotation update
    const localPlayer = gameState.players.find(p => p.id === localPlayerId)
    const isUnderDiscerningEye = localPlayer?.effectCards.some(
      c => c.type === CardType.DiscerningEye && c.appliedRound === gameState.round - 1
    )

    if (isUnderDiscerningEye && gameState.phase === GamePhase.Preparation) {
      // Sync rotation to opponent via game action
      executeGameAction(
        { type: 'ROTATE_PREPARED_CARD', playerId: localPlayerId, cardId, rotation: newRotation },
        (state) => gameLogic.rotatePreparedCard(state, localPlayerId, cardId, newRotation)
      )
    } else {
      // Standard local-only rotation update
      setGameState((prev: GameState | null) => {
        if (!prev) return null
        const playerIndex = prev.players.findIndex(p => p.id === localPlayerId)
        const newPlayers = [...prev.players] as [Player, Player]
        const player = newPlayers[playerIndex]
        const cardIndex = player.prepared.findIndex(pc => pc.card.id === cardId)
        if (cardIndex > -1) {
          const newPrepared = [...player.prepared]
          newPrepared[cardIndex] = { ...newPrepared[cardIndex], rotation: newRotation }
          newPlayers[playerIndex] = { ...player, prepared: newPrepared }
          return { ...prev, players: newPlayers }
        }
        return prev
      })
    }

    // Sync targeting state if this card is currently being targeted
    if (targeting && targeting.card && targeting.card.card.id === cardId) {
      const updatedCard = { ...targeting.card, rotation: newRotation }
      const effect = gameLogic.getCardEffect(updatedCard)
      if (!gameLogic.isTargetableEffect(effect.type)) {
        // Rotated to a non-targetable side, cancel targeting
        setTargeting(null)
        setMousePos(null)
      } else {
        // Update targeting with new rotation
        setTargeting({ ...targeting, card: updatedCard })
      }
    }
  }, [gameState, localPlayerId, setGameState, executeGameAction, targeting, setTargeting, setMousePos])

  const handleHandCardRotation = useCallback((cardId: string, newRotation: number) => {
    setHandCardRotations(prev => ({ ...prev, [cardId]: newRotation }))
  }, [setHandCardRotations])

  const handleReturnCardToHand = useCallback((cardId: string) => {
    if (!gameState || gameState.phase !== GamePhase.Preparation || playerIsReady) return
    executeGameAction(
      { type: 'RETURN_CARD', playerId: localPlayerId, cardId },
      (state) => gameLogic.returnCardToHand(state, localPlayerId, cardId)
    )
  }, [gameState, playerIsReady, localPlayerId, executeGameAction])

  const handleReorderHand = useCallback((draggedGroupKey: string, targetGroupKey: string) => {
    if (!gameState || !gameState.players) return
    const player = gameState.players.find(p => p.id === localPlayerId)!
    const groupedHand = groupHandForDisplay(player.hand)
    const draggedIndex = groupedHand.findIndex(g => g.key === draggedGroupKey)
    const targetIndex = groupedHand.findIndex(g => g.key === targetGroupKey)
    if (draggedIndex === -1 || targetIndex === -1) return
    const reorderedGroupedHand = [...groupedHand]
    const [draggedItemGroup] = reorderedGroupedHand.splice(draggedIndex, 1)
    reorderedGroupedHand.splice(targetIndex, 0, draggedItemGroup)
    const newHand = reorderedGroupedHand.flatMap(g => g.cards)
    const playerIndex = gameState.players.findIndex(p => p.id === localPlayerId)
    const newPlayer = { ...player, hand: newHand }
    const newPlayers = [...gameState.players] as [Player, Player]
    newPlayers[playerIndex] = newPlayer
    setGameState({ ...gameState, players: newPlayers })
    handleCardDragEnd() // Reset drag state
  }, [gameState, localPlayerId, groupHandForDisplay, setGameState, handleCardDragEnd])


  const handleCounterActivation = useCallback((cardId: string, rotation: number, targetActivationId?: string) => {
    if (!gameState || !currentActivation) return
    executeGameAction(
      { type: 'COUNTER_ACTIVATION', playerId: localPlayerId, cardId, rotation, targetActivationId },
      (state) => gameLogic.counterActivation(state, localPlayerId, cardId, rotation, targetActivationId)
    )
  }, [gameState, currentActivation, localPlayerId, executeGameAction])


  const handleShieldActivation = useCallback((cardId: string, rotation: number, targetActivationId?: string) => {
    if (!gameState || !gameState.activations || !gameState.players || gameState.activations.length === 0) return
    const player = gameState.players.find(p => p.id === localPlayerId)!
    const preparedCard = player.prepared.find(pc => pc.card.id === cardId)
    if (preparedCard && preparedCard.card.type === CardType.PlantedBomb) {
      // For Planted Bomb, find target activation by ID, or use last
      let targetActivation = gameState.activations[gameState.activations.length - 1]
      if (targetActivationId) {
        const found = gameState.activations.find(act => act.attackingCard.card.id === targetActivationId)
        if (found) targetActivation = found
      }
      const targetCard = targetActivation.counterChain.length > 0
        ? targetActivation.counterChain[targetActivation.counterChain.length - 1].card
        : targetActivation.attackingCard.card
      if (!targetCard.essenceCost) {
        return
      }
    }
    executeGameAction(
      { type: 'ATTEMPT_SHIELD_ACTIVATION', playerId: localPlayerId, cardId, rotation, targetActivationId },
      (state) => gameLogic.attemptShieldActivation(state, localPlayerId, cardId, rotation, targetActivationId)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleDropOnPrep = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const cardId = e.dataTransfer.getData('cardId')
    const source = e.dataTransfer.getData('source')
    const rotation = parseInt(e.dataTransfer.getData('rotation'), 10) || 0
    if (cardId && source === 'hand') {
      handlePrepareCard(cardId, rotation)
      handleCardDragEnd() // Force clear drag state
    }
  }, [handlePrepareCard, handleCardDragEnd])

  const handleDropOnActivationChain = useCallback((e: React.DragEvent<HTMLDivElement>, targetActivationId?: string) => {
    e.preventDefault()
    setShowDefenseGhost(false)
    if (!gameState || !currentActivation) return
    const cardId = e.dataTransfer.getData('cardId')
    const source = e.dataTransfer.getData('source')
    const rotation = parseInt(e.dataTransfer.getData('rotation'), 10) || 0

    // PHASE 5: Extract target activation ID from drag data if not passed directly
    const dragTargetId = e.dataTransfer.getData('targetActivationId')
    const finalTargetActivationId = targetActivationId ?? (dragTargetId || undefined)

    if (cardId && (source === 'prepared-reaction' || source === 'prepared')) {
      const localPlayer = gameState.players.find(p => p.id === localPlayerId)!
      const cardInLocalPlayerPrepared = localPlayer.prepared.find(pc => pc.card.id === cardId)
      const isBurglarySpell = !!(cardInLocalPlayerPrepared && cardInLocalPlayerPrepared.card.type === CardType.Steal && (cardInLocalPlayerPrepared.card as any).subType === 'Spell')
      if (cardInLocalPlayerPrepared && cardInLocalPlayerPrepared.card.extendedShield && !isBurglarySpell) {
        // Pass targetActivationId to handleShieldActivation for correct parent detection
        handleShieldActivation(cardId, rotation, finalTargetActivationId)
      } else {
        // PHASE 5: Pass targetActivationId to handleCounterActivation
        handleCounterActivation(cardId, rotation, finalTargetActivationId)
      }
      handleCardDragEnd() // Force clear drag state
    }
  }, [gameState, currentActivation, localPlayerId, setShowDefenseGhost, handleShieldActivation, handleCounterActivation, handleCardDragEnd])

  const handleDropOnHand = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const cardId = e.dataTransfer.getData('cardId')
    const source = e.dataTransfer.getData('source')
    if (cardId && source === 'prepared') handleReturnCardToHand(cardId)
  }, [handleReturnCardToHand])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => e.preventDefault(), [])


  const handleCounterDragEnter = useCallback(() => {
    if (!draggedCardInfo || !gameState || !currentActivation) return
    const targetCard = currentActivation.counterChain.length > 0
      ? currentActivation.counterChain[currentActivation.counterChain.length - 1]
      : currentActivation.attackingCard
    const tempPc = { card: draggedCardInfo.card as any, rotation: draggedCardInfo.rotation } as any
    const isProtector = draggedCardInfo.card.type === CardType.Protector
    const hasExtendedShield = !!(draggedCardInfo.card as any).extendedShield
    const isBurglarySpell = draggedCardInfo.card.type === CardType.Steal && (draggedCardInfo.card as any).subType === 'Spell'
    const canCounter = (isProtector || (hasExtendedShield && !isBurglarySpell)) ? true : isValidCounter(tempPc, targetCard)
    if (canCounter) setShowDefenseGhost(true)
  }, [draggedCardInfo, gameState, currentActivation, setShowDefenseGhost])

  const handleCounterDragLeave = useCallback(() => setShowDefenseGhost(false), [setShowDefenseGhost])

  const handleTargetableMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>, preparedCard: any) => {
    if (!gameState || gameState.phase !== GamePhase.Combat || gameState.turnPlayerId !== localPlayerId || currentActivation || gameState.discardMode?.[localPlayerId]) return
    const effect = gameLogic.getCardEffect(preparedCard)
    if (!gameLogic.isTargetableEffect(effect.type)) return

    // Prevent native drag behavior (ghost image) for targetable cards
    e.preventDefault()
    e.stopPropagation()

    const cardRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
    if (cardRect) {
      // IMPORTANT: If targeting is already set (e.g., from Protector mode choice), 
      // only update the mouse position, don't overwrite the card/rotation!
      if (targeting && targeting.card && targeting.card.card.id === preparedCard.card.id) {
        setMousePos({ x: e.clientX, y: e.clientY })
      } else {
        setTargeting({ card: preparedCard, startRect: cardRect } as any)
        setMousePos({ x: e.clientX, y: e.clientY })
      }
    }
  }, [gameState, localPlayerId, currentActivation, setTargeting, setMousePos, targeting])

  useEffect(() => {
    if (!gameState || !gameState.players) return
    const player = gameState.players.find(p => p.id === localPlayerId)!
    const opponent = gameState.players.find(p => p.id !== localPlayerId)!
    const handleMouseMove = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY })
    const handleMouseUp = (e: MouseEvent) => {
      const opponentAvatarRect = getOpponentAvatarRect()
      const playerAvatarRect = getLocalAvatarRect()
      let targetId: number | null = null
      if (opponentAvatarRect && e.clientX >= opponentAvatarRect.left && e.clientX <= opponentAvatarRect.right && e.clientY >= opponentAvatarRect.top && e.clientY <= opponentAvatarRect.bottom) {
        targetId = opponent.id
      } else if (playerAvatarRect && e.clientX >= playerAvatarRect.left && e.clientX <= playerAvatarRect.right && e.clientY >= playerAvatarRect.top && e.clientY <= playerAvatarRect.bottom) {
        targetId = player.id
      }

      // Use ref to get the LATEST targeting value (avoids stale closure)
      const currentTargeting = targetingRef.current;

      if (currentTargeting) {
        const src = (currentTargeting as any).source

        // Handle mage-counter source: Counterblast/Invincible targeting activation chains
        if (src === 'mage-counter' && typeof handleMageCastFromHandWithTarget === 'function') {
          const hitZone = activationZonesRef?.current.find(zone => {
            const tolerance = 20
            return e.clientX >= zone.rect.left - tolerance &&
              e.clientX <= zone.rect.right + tolerance &&
              e.clientY >= zone.rect.top - tolerance &&
              e.clientY <= zone.rect.bottom + tolerance
          })
          const targetActivationId = hitZone?.activationId
          const validActivationIds = (currentTargeting as any).validActivationIds as string[] | undefined

          if (targetActivationId && (!validActivationIds || validActivationIds.includes(targetActivationId))) {
            handleMageCastFromHandWithTarget(
              (currentTargeting as any).card.card.id,
              (currentTargeting as any).card.rotation,
              undefined, // no player targetId
              targetActivationId
            )
            if (typeof onMageCastSuccess === 'function') onMageCastSuccess()
          }
          setTargeting(null)
          setMousePos(null)
          return
        }

        // Handle protector-defense source: Protector Defense mode targeting activation chains
        if (src === 'protector-defense') {
          if (isDropInActivationArea(e.clientX, e.clientY) && gameState?.activations && gameState.activations.length > 0) {
            // Find target activation and call shield activation
            const targetActivation = gameState.activations.find(act => !act.resolved) || gameState.activations[gameState.activations.length - 1]
            const targetActivationId = targetActivation?.attackingCard.card.id

            // Call handleShieldActivation for Defense mode
            handleShieldActivation((currentTargeting as any).card.card.id, 0, targetActivationId)
          } else {
            // Missed targeting, return to choice modal
            setProtectorChoiceCardId((currentTargeting as any).card.card.id)
          }
          setTargeting(null)
          setMousePos(null)
          return
        }

        if (targetId !== null) {
          if (src === 'mage-hand' && typeof handleMageCastFromHandWithTarget === 'function') {
            handleMageCastFromHandWithTarget((currentTargeting as any).card.card.id, (currentTargeting as any).card.rotation, targetId)
            if (typeof onMageCastSuccess === 'function') onMageCastSuccess()
          } else if (src === 'astrid-hero' && typeof handleAstridBlastWithTarget === 'function') {
            const color = (currentTargeting as any).card.card.color as CardColor
            handleAstridBlastWithTarget(color, targetId)
          } else {
            const rotationFromTargeting = (currentTargeting as any).card.rotation;
            handleActivateCard((currentTargeting as any).card.card.id, rotationFromTargeting, targetId)
          }
        } else if (src === 'protector-attack') {
          // Missed targeting, return to choice modal
          setProtectorChoiceCardId((currentTargeting as any).card.card.id)
        }
      }
      setTargeting(null)
      setMousePos(null)
    }
    if (targeting) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      // Add pointer event listeners for touch devices
      window.addEventListener('pointerdown', handleMouseMove as any)
      window.addEventListener('pointermove', handleMouseMove as any)
      window.addEventListener('pointerup', handleMouseUp as any)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
        window.removeEventListener('pointerdown', handleMouseMove as any)
        window.removeEventListener('pointermove', handleMouseMove as any)
        window.removeEventListener('pointerup', handleMouseUp as any)
      }
    }
  }, [targeting, handleActivateCard, handleShieldActivation, setProtectorChoiceCardId, gameState, localPlayerId, setMousePos, setTargeting, getLocalAvatarRect, getOpponentAvatarRect, isDropInActivationArea, activationZonesRef])

  return {
    handlePrepareCard,
    handlePreparedCardRotation,
    handleHandCardRotation,
    handleReturnCardToHand,
    handleReorderHand,
    handleCounterActivation,
    handleShieldActivation,
    handleDropOnPrep,
    handleDropOnActivationChain,
    handleDropOnHand,
    handleDragOver,
    handleCardDragStart,
    handleCardDragEnd,
    handleCounterDragEnter,
    handleCounterDragLeave,
    handleTargetableMouseDown,
    handlePassTurn: () => {
      if (!gameState || gameState.phase !== GamePhase.Combat || currentActivation) return
      if (gameState.turnPlayerId !== localPlayerId) return
      executeGameAction(
        { type: 'PASS_TURN', playerId: localPlayerId },
        (state) => gameLogic.passTurn(state, localPlayerId)
      )
    },
    handleAcceptActivation: () => {
      if (!gameState || !gameState.activations || gameState.activations.length === 0) return
      executeGameAction(
        { type: 'ACCEPT_ACTIVATION', playerId: localPlayerId },
        (state) => gameLogic.acceptActivation(state, localPlayerId)
      )
    },
    handleToggleDiscardMode: () => {
      executeGameAction(
        { type: 'TOGGLE_DISCARD_MODE', playerId: localPlayerId },
        (state) => gameLogic.toggleDiscardMode(state, localPlayerId)
      )
    },
    handleDayDiscard: (cardId: string) => {
      const myMode = gameState.discardMode?.[localPlayerId]
      if (!gameState || (myMode !== 'day' && myMode !== 'night')) return
      executeGameAction(
        { type: 'DISCARD_CARD', playerId: localPlayerId, cardId, discardType: 'day' },
        (state) => gameLogic.discardCardForEssence(state, localPlayerId, cardId)
      )
    },
    handleDayPreparedDiscard: (cardId: string) => {
      // For discarding prepared color cards during day discard mode
      if (!gameState || gameState.discardMode?.[localPlayerId] !== 'day') return
      executeGameAction(
        { type: 'DISCARD_CARD', playerId: localPlayerId, cardId, discardType: 'prepared_color' },
        (state) => gameLogic.discardPreparedColorCardForEssence(state, localPlayerId, cardId)
      )
    },
    handleNightDiscard: (cardId: string) => {
      if (!gameState || !gameState.players) return
      const player = gameState.players.find(p => p.id === localPlayerId)!
      const isPreparedStash = player.prepared.some(pc => pc.card.id === cardId && !!pc.card.essenceCost)
      const isPreparedColor = player.prepared.some(pc => pc.card.id === cardId && !pc.card.essenceCost)
      if (isPreparedStash) {
        // Stash cards can be discarded anytime during combat (no discardMode check needed)
        // UPDATE: User request - Stash cards should ONLY burn if Essence Mode is active
        if (!gameState.discardMode?.[localPlayerId]) return

        executeGameAction(
          { type: 'DISCARD_CARD', playerId: localPlayerId, cardId, discardType: 'stash' },
          (state) => gameLogic.discardStashCardForEssence(state, localPlayerId, cardId)
        )
      } else if (isPreparedColor) {
        // Color cards require night discard mode (local UI check)
        if (gameState.discardMode?.[localPlayerId] !== 'night') return
        executeGameAction(
          { type: 'DISCARD_CARD', playerId: localPlayerId, cardId, discardType: 'prepared_color' },
          (state) => gameLogic.discardPreparedColorCardForEssence(state, localPlayerId, cardId)
        )
      }
    },

  }
}
